//
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import DocumentationForExpense from 'employeeDashboard/common/DocumentationForExpense'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

//
import { AppConfigurationKeys, DuplicatePremium, getAppConfiguration, getDuplicatePremium } from 'core'
import { uploadFile } from 'core/application/commons/fileStorage/S3FileUploader'
import getFamilyMembersApi, {
  FamilyMember,
} from 'core/application/employeeDashboard/getReimbursed/reimbursementRequest/familyMembers/getFamilyMembersApi'
import { PremiumForSaveResponse, getPremiumForSave } from 'core/application/expenses/getPremiumForSave'
import saveInsurancePremium, { SaveInsurancePremiumRequest } from 'core/application/expenses/saveInsurancePremium'
import { RecurringExpenseFrequency } from 'core/domain/expenses/ReccuringExpenseFrequency'
import { Attachment } from 'core/domain/expenses/attachment'

import IndividualCoverageInformation from 'common/components/benefit/Expenses/IndividualCoverageInformation'
import ConfirmModal from 'common/components/dataDisplay/ConfirmModal'
import { DatePicker, Input, TextArea } from 'common/components/dataEntry/formik'

import {
  Col,
  DefaultDatePickerV2,
  DefaultRadioGroupV2,
  Icon,
  Notification,
  Radio,
  RedAsterisk,
  Row,
  Skeleton,
} from 'common/components'
import { useCommandRequestV2, useLoginUserV2, useQueryParams, useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import MecExplanation from '../../../../common/components/benefit/Expenses/MecExplanation'
import TaxAdvantage from '../../../../common/components/benefit/Expenses/TaxAdvantageExpense'
import TaxFree from '../../../../common/components/benefit/Expenses/TaxFreeExpense'
import { formatMonth, toDateWithoutTimeZone } from '../../../../common/dateFormatters'
import { disclaimerModal } from '../../DisclaimerModal'
import SelectExpenseForFamilyMember from '../../SelectFamilyMember'
import Navigation from '../../navigation/Navigation'
import AddFamilyMemberModal from '../common/addFamilyMember/AddFamilyMemberModal'
import CoverageProviderSelect from '../common/coverageProviderSelect'
import DuplicateExpenseModal from '../common/duplicateExpenseModal'
import ExpenseAttachmentUploader from '../common/expenseAttachmentUploader'
import SelectExpenseForDependent from '../common/selectExpenseForDependent'
import SelectInsurancePlanReason from '../common/selectInsurancePlanReason'
import { SubmitButton } from '../saveExpense'

//
import './style.less'

export const REQUEST_FOR_PREMIUM_EXPENSE_REIMBURSE_PATH = 'submit-premium-expense'

interface UploadFileProps {
  file: File
  saveForLater?: boolean
  id: number
}

export const ME_VALUE: string = 'me'
export const ADD_FAMILY_MEMBER_VALUE: string = 'add'

const SubmitReimbursementRequest = () => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const history = useHistory()

  const params = useQueryParams()
  const expenseTypeId = +params.get('expenseTypeId')
  const planPeriodId = params.get('planPeriodId')
  const recurringExpenseId = params.get('recurringExpenseId') ?? null

  const { data: premiumForSave } = useQueryRequestV2<PremiumForSaveResponse>({
    initialState: null,
    queryFn: () => getPremiumForSave(planPeriodId, expenseTypeId, recurringExpenseId),
  })

  const [duplicatePremium, setDuplicatePremium] = useState<DuplicatePremium | null>(null)
  const { sendQuery: sendDuplicatePremiumQuery } = useQueryRequestV2<DuplicatePremium>()

  const [valuesToSubmit, setValuesToSubmit] = useState<SaveInsurancePremiumRequest | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { data: familyMembers, reload: reloadFamilyMembers } = useQueryRequestV2<FamilyMember[]>({
    initialState: [],
    queryFn: () => getFamilyMembersApi(),
  })
  const { sendCommand: sendSavePremium } = useCommandRequestV2((valuesToSubmit: SaveInsurancePremiumRequest) =>
    saveInsurancePremium(valuesToSubmit),
  )
  const { sendCommand: sendUploadFile } = useCommandRequestV2((uploadItem: UploadFileProps) =>
    uploadFile(uploadItem.file),
  )
  const { data: docReviewTurnAroundTime } = useQueryRequestV2({
    queryFn: () => getAppConfiguration(AppConfigurationKeys.DocReviewTurnAroundInformation),
  })
  const { user } = useLoginUserV2()

  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false)
  const [addNewFamilyMemberVisible, setAddNewFamilyMemberVisible] = useState<boolean>(false)
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<UploadFileProps[]>([])
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([])
  const [initialValues, setInitialValues] = useState<FormData | null>(null)
  const [familyMemberName, setFamilyMemberName] = useState<string | null>(null)

  const chooseExpenseTypeUrl = `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_GET_REIMBURSED}/${planPeriodId}`

  useEffect(() => {
    if (premiumForSave) {
      setInitialValues({
        ...premiumForSave,
        startDate: recurringExpenseId ? premiumForSave.startDate : null,
        requestedAmount: recurringExpenseId ? premiumForSave.requestedAmount : null,
        frequency: recurringExpenseId ? premiumForSave.frequency : null,
        familyMemberId: premiumForSave.familyMemberId ? premiumForSave.familyMemberId ?? ME_VALUE : undefined,
        requiresAttestationConfirmationToSubmitExpense: premiumForSave.requiresAttestationConfirmationToSubmitExpense,
        familyMemberCoveredByThisPolicyIds: [],
      })
    }
  }, [premiumForSave])

  const disclaimer = () => {
    disclaimerModal(() => history.push(chooseExpenseTypeUrl), translate)
  }

  const handleSubmit = async (values: SaveInsurancePremiumRequest) =>
    await Promise.all(
      filesToBeUploaded.map(async (uploadItem) => {
        const result = await sendUploadFile(uploadItem)

        if (result.completed) {
          values.attachments.push({
            filename: result.data.fileId,
            reuseInOtherExpenses: uploadItem.saveForLater ?? false,
            title: uploadItem.file.name,
          })
        }
      }),
    ).then(async () => {
      const result = await sendSavePremium(values)
      setIsSubmitting(false)

      if (result.completed) {
        Notification.open({
          type: 'success',
          message: translate('get-reimburse.request.expense-added-successfully-message')
            .replace('[AMOUNT]', values.requestedAmount.toString())
            .replace('[TURN_AROUND_TIME]', docReviewTurnAroundTime.value),
        })

        history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
      }
    })

  const onSubmit = async (formikValues: any) => {
    setIsSubmitting(true)

    const values: SaveInsurancePremiumRequest = {
      ...formikValues,
      recurringExpenseId: recurringExpenseId,
      familyMemberId: formikValues.familyMemberId === ME_VALUE ? undefined : formikValues.familyMemberId,
      planPeriodId: planPeriodId,
      expenseTypeId: expenseTypeId,
      doesThisPolicyProvidesMec: formikValues.mec,
      attachments: selectedAttachments.map((attachment) => ({
        id: attachment.attachmentId,
        filename: attachment.title,
        reuseInOtherExpenses: attachment.reuseInOtherExpenses,
        title: attachment.title,
      })),
      familyMemberCoveredByThisPolicyIds: formikValues.familyMemberCoveredByThisPolicyIds?.filter(
        (x) => x !== '' && x !== 'me',
      ),
    }

    setValuesToSubmit(values)

    const duplicate = await sendDuplicatePremiumQuery(() =>
      getDuplicatePremium({
        policyName: formikValues.name,
        requestedAmount: formikValues.requestedAmount,
        startDate: formikValues.startDate,
      }),
    )
    if (duplicate) {
      setDuplicatePremium(duplicate)
    } else handleSubmit(values)
  }

  const areFilesToBeUploadedValid = () => {
    return filesToBeUploaded.length > 0 || selectedAttachments.length > 0
  }

  const isSectionEnabled = (formik: FormikProps<FormData>) =>
    attestationFormValidator.isValidSync(formik.values) ||
    !formik.values?.requiresAttestationConfirmationToSubmitExpense

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={() => {}}
      validate={(values) => {
        setHasDirtyData(true)
      }}
    >
      {(formik) => {
        return (
          <Navigation
            title={`${translate('get-reimburse.request.get-reimbursed-label')} ${
              premiumForSave?.expenseType.name ?? ''
            }`}
            redirectPath={chooseExpenseTypeUrl}
            disclaimerModal={hasDirtyData ? disclaimer : undefined}
          >
            <Skeleton active loading={false}>
              <form onSubmit={formik.handleSubmit} autoComplete="off" className="w-100 premium-reimbursement-request">
                <Row>
                  {/* Left Column */}
                  <Col md={24} lg={13}>
                    <Row>
                      {initialValues?.requiresAttestationConfirmationToSubmitExpense && (
                        <Col span={24} className="bg-gray-2 p-24 mb-24">
                          <Row>
                            <Col span={24}>
                              <h5 className="section_title mb-32">Premium expense attestation</h5>
                            </Col>
                          </Row>
                          <Row className="field-row mb-24">
                            <Col md={24} xl={15}>
                              <SelectExpenseForFamilyMember
                                label="Who is the primary policy holder on this plan?"
                                inputName="familyMemberId"
                                familyMembers={familyMembers}
                                onChange={(value) => {
                                  value === ADD_FAMILY_MEMBER_VALUE && setAddNewFamilyMemberVisible(true)
                                  formik.setValues({
                                    ...formik.values,
                                    familyMemberId: value,
                                    startDate: null,
                                    coveredByAnIndividualHealthInsurancePolicy: null,
                                    familyMemberCoveredByThisPolicyIds:
                                      formik.values.familyMemberCoveredByThisPolicyIds?.filter((x) => x !== value),
                                  })
                                  setFamilyMemberName(
                                    value === ME_VALUE
                                      ? user?.firstName
                                      : familyMembers?.find((member) => member.id === value)?.firstName ?? null,
                                  )
                                }}
                              />
                            </Col>
                          </Row>
                          {formik.values?.familyMemberId && (
                            <>
                              <Row className="field-row mb-24">
                                <Col md={24} xl={15}>
                                  <SelectExpenseForDependent
                                    name="familyMemberCoveredByThisPolicyIds"
                                    isForSelf={familyMemberName === user?.firstName}
                                    familyMembers={familyMembers.filter((x) => x.id !== formik.values.familyMemberId)}
                                  />
                                </Col>
                              </Row>
                              <Row className="field-row mb-24">
                                <Col md={24} xl={15}>
                                  <DefaultDatePickerV2
                                    label="On what date this insurance coverage start?"
                                    showRequiredSign
                                    name="startDate"
                                    allowClear={false}
                                    disabledDate={(d) =>
                                      !d ||
                                      d.isAfter(toDateWithoutTimeZone(initialValues.maxPurchasedDate), 'day') ||
                                      d.isBefore(toDateWithoutTimeZone(initialValues.minPurchasedDate), 'day')
                                    }
                                  />
                                  <span className="color-gray-7 mb-8">
                                    This can be different from the day you paid it.
                                  </span>
                                </Col>
                              </Row>
                            </>
                          )}

                          {formik.values?.familyMemberId && (
                            <Row className="field-row">
                              <Col md={24} xl={12}>
                                <DefaultRadioGroupV2
                                  label={
                                    <div className="flex flex__center color-gray-9 mb-8">
                                      <RedAsterisk className="mr-4" />
                                      <div>
                                        Is this a qualifying individual health insurance plan, or was{' '}
                                        <span className="text-bold">{familyMemberName} </span>covered by a different
                                        qualifying individual health insurance plan when this insurance coverage
                                        started?
                                      </div>
                                    </div>
                                  }
                                  type={'radio'}
                                  name={'coveredByAnIndividualHealthInsurancePolicy'}
                                  direction={'horizontal'}
                                  items={[
                                    { name: 'Yes', value: true },
                                    { name: 'No', value: false },
                                  ]}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    if (!value) setConfirmModalVisible(true)
                                  }}
                                ></DefaultRadioGroupV2>
                              </Col>
                            </Row>
                          )}
                        </Col>
                      )}
                      {/* Expense Details Section */}
                      {isSectionEnabled(formik) && (
                        <Col span={24} className="bg-gray-2 p-24 mb-24">
                          <Row>
                            <Col span={24}>
                              <h5 className="section_title mb-32">
                                {translate('get-reimburse.request.expense-details-label')}
                              </h5>
                            </Col>
                          </Row>

                          <Row className="field-row mb-24">
                            <Col span={24}>
                              <Input
                                label={
                                  <span>
                                    <RedAsterisk />
                                    {translate('get-reimburse.premium-expense-form.policy-name-input')}
                                  </span>
                                }
                                placeholder={translate('get-reimburse.premium-expense-form.policy-name-placeholder')}
                                name="name"
                                type="text"
                              />
                            </Col>
                          </Row>

                          {!formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                            <Row className="field-row mb-24">
                              <Col md={24} xl={15}>
                                <SelectExpenseForFamilyMember
                                  inputName="familyMemberId"
                                  familyMembers={familyMembers}
                                  onChange={(value) => {
                                    if (value === ADD_FAMILY_MEMBER_VALUE) {
                                      setAddNewFamilyMemberVisible(true)
                                    }
                                  }}
                                />
                              </Col>
                            </Row>
                          )}

                          <Row className="field-row mb-24">
                            <Col md={24} xl={12}>
                              <Input
                                label={
                                  <span>
                                    <RedAsterisk className="mr-4" />
                                    {translate('get-reimburse.premium-expense-form.premium-amount-input')}
                                  </span>
                                }
                                name="requestedAmount"
                                placeholder={translate('get-reimburse.premium-expense-form.premium-amount-placeholder')}
                                prefix={<Icon type="fa fa-usd" className="mr-4" fontAwesome />}
                              />
                            </Col>
                          </Row>

                          {!formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                            <Row className="field-row mb-24">
                              <Col md={24} xl={15}>
                                <DatePicker
                                  label={translate('get-reimburse.premium-expense-form.purchased-date-input')}
                                  showRequiredSign
                                  name="startDate"
                                  id="startDate"
                                  noStyles
                                  className="date_of_service_datepicker w-100"
                                  placeholder={translate('get-reimburse.request.select-date-label')}
                                  disabledDate={(d) =>
                                    !d ||
                                    d.isAfter(toDateWithoutTimeZone(initialValues.maxPurchasedDate), 'day') ||
                                    d.isBefore(toDateWithoutTimeZone(initialValues.minPurchasedDate), 'day')
                                  }
                                />
                                <span className="color-gray-7 mb-8">
                                  {translate('get-reimburse.premium-expense-form.purchased-date-input')}
                                </span>
                              </Col>
                            </Row>
                          )}

                          <Row className="field-row mb-16">
                            <Col span={24}>
                              <Row gutter={8}>
                                <Col span={24}>
                                  <div className="flex mb-8">
                                    <RedAsterisk className="mr-4" />
                                    <span>{translate('get-reimburse.premium-expense-form.purchase-type-input')}</span>
                                  </div>
                                </Col>
                              </Row>
                              <Radio.Group name="frequency" className="w-100">
                                <Row gutter={8}>
                                  <Col md={24} xl={8} className="mb-8">
                                    <Radio.Button value={RecurringExpenseFrequency.Monthly}>
                                      {translate('get-reimburse.premium-expense-form.purchase-type-option-1')}
                                    </Radio.Button>
                                  </Col>
                                  <Col md={24} xl={8} className="mb-8">
                                    <Radio.Button value={RecurringExpenseFrequency.Quarterly}>
                                      {translate('get-reimburse.premium-expense-form.purchase-type-option-2')}
                                    </Radio.Button>
                                  </Col>
                                  <Col md={24} xl={8} className="mb-8">
                                    <Radio.Button value={RecurringExpenseFrequency.Annually}>
                                      {translate('get-reimburse.premium-expense-form.purchase-type-option-3')}
                                    </Radio.Button>
                                  </Col>
                                </Row>
                                {[209, 210, 211].includes(expenseTypeId) && (
                                  <Row gutter={8}>
                                    <Col md={24} xl={8} className="mb-8">
                                      <Radio.Button value={RecurringExpenseFrequency.Weekly}>
                                        {translate('get-reimburse.premium-expense-form.purchase-type-option-4')}
                                      </Radio.Button>
                                    </Col>
                                    <Col md={24} xl={8} className="mb-8">
                                      <Radio.Button value={RecurringExpenseFrequency.BiWeekly}>
                                        {translate('get-reimburse.premium-expense-form.purchase-type-option-5')}
                                      </Radio.Button>
                                    </Col>
                                    <Col md={24} xl={8} className="mb-8">
                                      <Radio.Button value={RecurringExpenseFrequency.TwiceMonthly}>
                                        {translate('get-reimburse.premium-expense-form.purchase-type-option-6')}
                                      </Radio.Button>
                                    </Col>
                                  </Row>
                                )}
                              </Radio.Group>
                            </Col>
                          </Row>
                          <Row className="field-row mb-24">
                            <Col md={24} xl={15}>
                              <SelectInsurancePlanReason inputName="healthPlanSource" />
                            </Col>
                          </Row>
                          <Row className="field-row">
                            <Col span={24}>
                              <TextArea
                                label={translate('get-reimburse.request.expense-description-label')}
                                name="description"
                                cols={10}
                                placeholder={translate('get-reimburse.request.expense-description-placeholder')}
                              />
                            </Col>
                          </Row>
                        </Col>
                      )}
                      {/* End Expense Details Section */}

                      {/* Expense Receipt Section */}
                      {isSectionEnabled(formik) && (
                        <Col span={24} className="bg-gray-2 p-24 mb-24">
                          <Row>
                            <Col span={24}>
                              <h5 className="section_title mb-32">
                                {translate('get-reimburse.request.expense-reciept-label')}
                              </h5>
                            </Col>
                          </Row>
                          <h6 className="add_label mb-8">
                            <RedAsterisk className={'mr-4'} />
                            {translate('get-reimburse.request.add-documentation-label')}
                          </h6>
                          <DocumentationForExpense
                            eligibility={initialValues?.expenseType.eligibility}
                            companyName={initialValues?.expenseType.name}
                            translate={translate}
                          />
                          <Row className="field-row">
                            <Col span={24}>
                              <ExpenseAttachmentUploader
                                initialValues={selectedAttachments}
                                onSelectedAttachmentsChanged={(newAttachments) =>
                                  setSelectedAttachments(newAttachments)
                                }
                                onUploadedFilesChanged={(files) => setFilesToBeUploaded(files)}
                              />
                            </Col>
                          </Row>
                        </Col>
                      )}
                      {/* End Expense Receipt Section */}
                    </Row>
                  </Col>
                  {/* End Left Column */}

                  {/* Right Column */}
                  {formik.values?.startDate && !formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                    <Col md={24} lg={{ span: 10, offset: 1 }} xl={{ span: 9, offset: 2 }}>
                      <div className={'bg-gray-2 p-24'}>
                        <Row gutter={8}>
                          <Col span={24}>
                            <div className="flex mb-8">
                              <RedAsterisk className="mr-4" />
                              <span className={'mr-8'}>Does this policy provide minimum essential coverage (MEC)?</span>
                            </div>
                          </Col>
                        </Row>

                        <Radio.Group name="mec" className="w-100 mb-24">
                          <Row gutter={16}>
                            <Col span={4}>
                              <Row gutter={8}>
                                <Col span={24}>
                                  <Radio value={true}>
                                    {translate('get-reimburse.premium-expense-form.minimum-coverage-yes')}
                                  </Radio>
                                </Col>
                                <Col span={24}>
                                  <Radio value={false}>
                                    {translate('get-reimburse.premium-expense-form.minimum-coverage-no')}
                                  </Radio>
                                </Col>
                              </Row>
                            </Col>
                            <Col span={20}>{formik.values?.mec !== undefined && formik.values?.mec && <TaxFree />}</Col>
                          </Row>
                        </Radio.Group>

                        {formik.values?.mec !== undefined && !formik.values?.mec && (
                          <>
                            <Row gutter={8}>
                              <Col span={24}>
                                <div className="flex mb-8">
                                  <RedAsterisk className="mr-4" />
                                  <span className={'mr-8'}>
                                    In <b>{formatMonth(formik.values?.startDate)}</b>, were you covered by health
                                    insurance providing minimum essential coverage(MEC)?
                                  </span>
                                </div>
                              </Col>
                            </Row>

                            <Radio.Group name="anotherPolicyWithMec" className="w-100 mb-24">
                              <Row gutter={16}>
                                <Col span={24} className="flex items-center flex__wrap">
                                  <Row gutter={8}>
                                    <Col span={24}>
                                      <Radio value={true}>
                                        {translate('get-reimburse.premium-expense-form.minimum-coverage-yes')}
                                      </Radio>
                                    </Col>
                                    <Col span={24}>
                                      <Radio value={false}>
                                        {translate('get-reimburse.premium-expense-form.minimum-coverage-no')}
                                      </Radio>
                                    </Col>
                                  </Row>
                                  {formik.values?.anotherPolicyWithMec != undefined &&
                                    formik.values?.anotherPolicyWithMec && <TaxFree />}
                                  {formik.values?.anotherPolicyWithMec != undefined &&
                                    !formik.values?.anotherPolicyWithMec && <TaxAdvantage />}
                                </Col>
                              </Row>
                            </Radio.Group>
                          </>
                        )}

                        {!formik.values?.mec &&
                          formik.values?.anotherPolicyWithMec != undefined &&
                          formik.values?.anotherPolicyWithMec && (
                            <CoverageProviderSelect
                              name={'coverageProvider'}
                              planPeriodId={planPeriodId}
                              date={formik.values?.startDate}
                              familyMemberId={
                                formik.values.familyMemberId != ME_VALUE ? formik.values.familyMemberId : null
                              }
                            />
                          )}
                      </div>
                      <MecExplanation />
                    </Col>
                  )}
                  {formik.values?.requiresAttestationConfirmationToSubmitExpense && (
                    <Col md={24} lg={{ span: 10, offset: 1 }} xl={{ span: 9, offset: 2 }}>
                      <IndividualCoverageInformation />
                    </Col>
                  )}
                  {/* End Right Column */}

                  {/* Bottom Actions Column */}
                  <Col span={24}>
                    <Row className="bottom-actions-row">
                      <Col span={24} className="bottom-actions pt-8 pb-8 pl-24 pr-24">
                        {formik.isValid && areFilesToBeUploadedValid() && (
                          <span className="mr-8 color-gray-7">{translate('get-reimburse.request.submit-info')}</span>
                        )}
                        <SubmitButton
                          onClick={async () => {
                            await onSubmit(formik.values)
                          }}
                          isValid={formik.isValid && areFilesToBeUploadedValid()}
                          loading={isSubmitting}
                        />
                      </Col>
                    </Row>
                  </Col>
                  {/* End Bottom Actions Column */}
                </Row>
              </form>
              <AddFamilyMemberModal
                visible={addNewFamilyMemberVisible}
                onCancel={() => {
                  setAddNewFamilyMemberVisible(false)
                }}
                afterSuccessAdd={(familyMemberId: string) => {
                  reloadFamilyMembers().then(() => {
                    Notification.open({
                      type: 'success',
                      message: translate('get-reimburse.request.new-family-memeber-added'),
                    })
                    setAddNewFamilyMemberVisible(false)
                    formik.setFieldValue('familyMemberId', familyMemberId)
                  })
                }}
              />
              <DuplicateExpenseModal
                duplicateExpense={duplicatePremium}
                visible={duplicatePremium !== null}
                onCancel={(cancelSubmit) => {
                  setDuplicatePremium(null)
                  cancelSubmit && setIsSubmitting(false)
                }}
                onOk={() => handleSubmit(valuesToSubmit).then(() => pendo.track('Duplicate Premium'))}
              />
              <ConfirmModal
                title="Expense is ineligible..."
                content="For this expense to be eligible for reimbursement, the person who incurred the expense must have been covered by a qualifying individual health insurance plan at the time the expense was incurred."
                visible={confirmModalVisible}
                onCancel={() => {
                  setConfirmModalVisible(false)
                  formik.setFieldValue('coveredByAnIndividualHealthInsurancePolicy', null)
                }}
                onConfirm={() => history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)}
              />
            </Skeleton>
          </Navigation>
        )
      }}
    </Formik>
  )
}

type FormData = PremiumForSaveResponse & {
  mec?: boolean
  anotherPolicyWithMec?: boolean
  coveredByAnIndividualHealthInsurancePolicy?: boolean
}

export const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    name: Yup.string().nullable().required(translate('translation:general.required')),
    familyMemberId: Yup.string()
      .trim()
      .typeError(translate('translation:general.required'))
      .notOneOf([ADD_FAMILY_MEMBER_VALUE], translate('translation:general.required'))
      .required(translate('translation:general.required')),
    familyMemberCoveredByThisPolicyIds: Yup.array().nullable(),
    requestedAmount: Yup.number().nullable().required(translate('translation:general.required')),
    frequency: Yup.number().nullable().required(translate('translation:general.required')),
    startDate: Yup.date().nullable().required(translate('translation:general.required')),
    mec: Yup.boolean().when('requiresAttestationConfirmationToSubmitExpense', {
      is: (requiresAttestationConfirmationToSubmitExpense: boolean) => !requiresAttestationConfirmationToSubmitExpense,
      then: Yup.boolean().required(translate('translation:general.required')),
    }),
    anotherPolicyWithMec: Yup.boolean().when('mec', {
      is: (mec: boolean) => mec === false,
      then: Yup.boolean().required(translate('translation:general.required')),
    }),
    healthPlanSource: Yup.number().required(translate('translation:general.required')),
    coverageProvider: Yup.string()
      .nullable()
      .when('requiresAttestationConfirmationToSubmitExpense', {
        is: (requiresAttestationConfirmationToSubmitExpense: boolean) =>
          !requiresAttestationConfirmationToSubmitExpense,
        then: Yup.string().when('anotherPolicyWithMec', {
          is: (anotherPolicyWithMec: boolean) => anotherPolicyWithMec === true,
          then: Yup.string().required(translate('translation:general.required')),
        }),
      }),
  })

const attestationFormValidator = Yup.object({
  familyMemberId: Yup.string().trim().notOneOf([ADD_FAMILY_MEMBER_VALUE]).required(),
  startDate: Yup.date().nullable().required(),
  coveredByAnIndividualHealthInsurancePolicy: Yup.boolean().required().oneOf([true]),
})

export default SubmitReimbursementRequest
